import React, { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import type { ManagerAgentAnalyticsItem } from 'services/api/getManagerAgentAnalytics';
import { buildTransferOutcomeChartData } from './transfersVsOutcomesChart.utils';

import styles from './TransfersVsOutcomesChart.module.scss';

const ROW_HEIGHT = 36;
const MIN_CHART_HEIGHT = 220;
const Y_AXIS_WIDTH = 120;

const tooltipContentStyle = {
    backgroundColor: 'var(--slai-ui-white, #fff)',
    border: '1px solid var(--slai-ui-border-light, #eee)',
    borderRadius: 'var(--slai-ui-border-radius-sm, 4px)',
};

export type TransfersVsOutcomesChartProps = {
    agents: Array<Pick<ManagerAgentAnalyticsItem, 'id' | 'name' | 'agentPerformance'>>;
};

export const TransfersVsOutcomesChart: React.FC<TransfersVsOutcomesChartProps> = ({ agents }) => {
    const data = useMemo(() => buildTransferOutcomeChartData(agents), [agents]);
    const chartHeight = Math.max(MIN_CHART_HEIGHT, data.length * ROW_HEIGHT + 48);

    if (data.length === 0) {
        return <p className={styles.empty}>No agent transfer data for this period</p>;
    }

    return (
        <div
            className={styles.card}
            data-testid='transfers-vs-outcomes-chart'
            aria-label='Transfers vs outcomes by agent'
        >
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart
                    data={data}
                    layout='vertical'
                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    barCategoryGap='28%'
                >
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='var(--slai-ui-border-light)' />
                    <XAxis
                        type='number'
                        allowDecimals={false}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 11 }}
                    />
                    <YAxis
                        type='category'
                        dataKey='name'
                        width={Y_AXIS_WIDTH}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                        formatter={(value: number, name: string) => [value, name]}
                        labelFormatter={(label) => String(label)}
                        contentStyle={tooltipContentStyle}
                    />
                    <Legend
                        verticalAlign='top'
                        align='right'
                        iconType='square'
                        wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
                    />
                    <Bar
                        dataKey='completed'
                        name='Completed transfers'
                        stackId='outcomes'
                        fill='var(--slai-ui-primary-purple)'
                        radius={[0, 0, 0, 0]}
                    />
                    <Bar
                        dataKey='otherOutcomes'
                        name='Other outcomes'
                        stackId='outcomes'
                        fill='var(--slai-ui-primary-purple-light)'
                        radius={[0, 4, 4, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default TransfersVsOutcomesChart;
